/**
 * Jovii — THE WORDMARK'S LETTER TABLE, in one place.
 *
 * The wordmark is not a word set in a web font and it is not one image. It is
 * five letter tiles (`/brand/intro/w-{J,O,V,I}.webp`, the second I re-using
 * the first's tile) positioned from geometry measured off the locked artwork,
 * so that the J the intro flies in IS the J of the finished word — the same
 * element, moved. Logo.svelte, Intro.svelte and BrandSplash.svelte all draw it.
 *
 * WHY THIS MODULE EXISTS. Each of those three components used to carry its own
 * copy of the table and its own `WORD_ASPECT = 3.2278`, "inlined so the intro
 * draws on its first frame". That reasoning was sound for a fetch and wrong for
 * a module: a JSON import is bundled, costs no round trip, and cannot drift.
 * The copies drifted anyway — three hand-rounded tables and one canonical file
 * that nobody read at runtime — and the JOVI -> JOVII change would have been a
 * fourth chance for one of them to be missed. Now `wordmark.json` is the single
 * source and this file is the only reader of it.
 *
 * THE NUMBERS. `x` and `w` are fractions of the WHOLE WORD's width; `y` and `h`
 * are fractions of its height. `aspect` is the word's width over its height, so
 * a word `h` px tall is `h * WORD_ASPECT` px wide, and every letter's pixel box
 * is its fraction times that. The last letter's right edge is exactly 1.
 *
 * THE SECOND I. The brief for the rename fixed the spacing: the gap between
 * the two I's is 0.55 x the I glyph's width (211 px at the canonical 1422 px
 * tall word, where the I is 384 px wide). "I2" in the table is that entry; it
 * has no tile of its own. The convention — the letter's name minus any trailing
 * digits is its glyph file — is what `glyphOf()` implements, so a third I would
 * need nothing but a table row.
 */
import table from '../../../public/brand/intro/wordmark.json';

export interface Letter {
  /** The table key: `J`, `O`, `V`, `I`, `I2`. Unique; used as the render key. */
  readonly n: string;
  /** The tile that draws it: the name minus trailing digits (`I2` -> `I`). */
  readonly glyph: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** `I2` -> `I`: a letter that repeats re-uses the first one's tile. */
export const glyphOf = (name: string): string => name.replace(/\d+$/, '');

/** The letters in reading order, left to right, as the JSON lists them. */
export const LETTERS: readonly Letter[] = Object.entries(table.letters).map(([n, g]) => ({
  n,
  glyph: glyphOf(n),
  x: g.x,
  y: g.y,
  w: g.w,
  h: g.h
}));

/** Width over height of the whole word. ~3.646 for JOVII. */
export const WORD_ASPECT: number = table.aspect;

/** The first letter: the one the ident animates, and the mark on its own. */
export const J: Letter = LETTERS[0]!;

/**
 * The distinct tile files the word needs — `['J', 'O', 'V', 'I']` — for anything
 * that precaches or preloads them (the service worker's shell list).
 */
export const GLYPHS: readonly string[] = [...new Set(LETTERS.map((l) => l.glyph))];

/**
 * The URL of a letter tile. The version tag is the intro key: bumping it busts
 * the tile cache for every consumer at once, which is why it is a parameter
 * here rather than imported — `vite.config.ts` needs the bare path.
 */
export const glyphSrc = (glyph: string, version?: string): string =>
  `/brand/intro/w-${glyph}.webp${version ? `?v=${version}` : ''}`;
